import { useCallback, useEffect, useState } from 'react';

/**
 * iter-41: PWA install-prompt state machine.
 *
 * Detection paths
 * ---------------
 * - Modern Chromium (Android Chrome, Edge, desktop): the browser fires
 *   `beforeinstallprompt` when the manifest + service worker satisfy the
 *   install criteria. We capture the event, suppress the default mini-
 *   infobar, and surface our own branded bottom sheet so the install CTA
 *   doesn't feel like an alien browser dialog.
 *
 * - iOS Safari: no `beforeinstallprompt` support, but the PWA can still
 *   be added via the Share sheet. We detect iOS Safari and show a
 *   step-by-step illustration instead.
 *
 * Suppression
 * -----------
 * - `(display-mode: standalone)` or `navigator.standalone` → already
 *   installed, never prompt.
 * - `appinstalled` event → flip to installed and hide.
 * - Dismissed this session → 7-day cooldown (localStorage).
 * - "Don't show again" → never (localStorage flag).
 */

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

export type InstallPromptState =
  | 'idle'         // not enough signal yet
  | 'available'    // beforeinstallprompt captured → can prompt natively
  | 'ios'          // iOS Safari → manual Share-sheet instructions
  | 'dismissed'    // user dismissed (session cooldown or "never")
  | 'installed';   // already running as standalone

const DISMISS_KEY = 'yusr.installPrompt.dismissedAt';
const NEVER_KEY   = 'yusr.installPrompt.never';
const COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000;

export interface UseInstallPromptResult {
  state: InstallPromptState;
  promptInstall(): Promise<void>;
  dismiss(remember?: 'session' | 'forever'): void;
}

export function useInstallPrompt(): UseInstallPromptResult {
  const [state, setState] = useState<InstallPromptState>('idle');
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const isStandalone =
      window.matchMedia?.('(display-mode: standalone)').matches ||
      (window.navigator as { standalone?: boolean }).standalone === true;
    if (isStandalone) {
      setState('installed');
      return;
    }

    if (window.localStorage.getItem(NEVER_KEY) === '1') {
      setState('dismissed');
      return;
    }
    const dismissedAt = window.localStorage.getItem(DISMISS_KEY);
    if (dismissedAt && Date.now() - Number(dismissedAt) < COOLDOWN_MS) {
      setState('dismissed');
      return;
    }

    // iOS Safari path: detect iPhone/iPad + Safari (not Chrome/Firefox-on-iOS).
    const ua = window.navigator.userAgent;
    const isIOS = /iPhone|iPad|iPod/.test(ua) && !('MSStream' in window);
    const isSafari = /^((?!chrome|android|crios|fxios|edgios).)*safari/i.test(ua);
    if (isIOS && isSafari) {
      setState('ios');
      return;
    }

    const onBeforeInstall = (e: Event) => {
      e.preventDefault();
      setDeferred(e as BeforeInstallPromptEvent);
      setState('available');
    };
    const onInstalled = () => {
      setState('installed');
      setDeferred(null);
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const promptInstall = useCallback(async () => {
    if (!deferred) return;
    try {
      await deferred.prompt();
      const choice = await deferred.userChoice;
      if (choice.outcome === 'accepted') {
        setState('installed');
      } else {
        setState('dismissed');
        window.localStorage.setItem(DISMISS_KEY, String(Date.now()));
      }
    } finally {
      setDeferred(null);
    }
  }, [deferred]);

  const dismiss = useCallback((remember: 'session' | 'forever' = 'session') => {
    setState('dismissed');
    if (remember === 'forever') {
      window.localStorage.setItem(NEVER_KEY, '1');
    } else {
      window.localStorage.setItem(DISMISS_KEY, String(Date.now()));
    }
  }, []);

  return { state, promptInstall, dismiss };
}
